// Admin → "Content & Stats" tab. Edits app-wide CMS content (welcome, legal,
// contact, parental, push notification) and shows watch-log analytics.
// Self-contained — receives auth header from parent via props.
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FIELDS = [
  ["disclaimer_text",      "Startup disclaimer (blocking popup, HTML)", "textarea"],
  ["welcome_msg_screen",   "Welcome message",       "textarea"],
  ["expire_msg_screen",    "Trial expired message", "textarea"],
  ["legal_notices",        "Legal notices (HTML)",  "textarea"],
  ["contact_us",           "Contact us (HTML)",     "textarea"],
  ["parental_control_txt", "Parental control text", "textarea"],
];

export default function AdminContentTab({ authHeader }) {
  const [content, setContent] = useState(null);
  const [saving, setSaving]   = useState(false);
  const [stats, setStats]     = useState(null);
  const [logs, setLogs]       = useState({ items: [], total: 0 });
  const [wallpapers, setWallpapers] = useState([]);
  const [uploading, setUploading]   = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [c, s, l, w] = await Promise.all([
        axios.get(`${API}/admin/content`, { headers: authHeader }),
        axios.get(`${API}/admin/watch-log/stats?days=7`, { headers: authHeader }),
        axios.get(`${API}/admin/watch-log?limit=25`, { headers: authHeader }),
        axios.get(`${API}/wallpapers`),
      ]);
      setContent(c.data);
      setStats(s.data);
      setLogs(l.data);
      setWallpapers(w.data.items || []);
    } catch (e) {
      toast.error("Failed to load content: " + (e?.response?.data?.detail || e.message));
    }
  }, [authHeader]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const patch = (k, v) => setContent((c) => ({ ...c, [k]: v }));

  const save = async (extra = {}) => {
    if (!content) return;
    setSaving(true);
    try {
      const payload = { ...extra };
      for (const [k] of FIELDS) if (content[k] !== undefined) payload[k] = content[k];
      payload.trial_days = Number(content.trial_days || 7);
      const { data } = await axios.put(`${API}/admin/content`, payload, { headers: authHeader });
      setContent(data);
      toast.success("Content saved");
    } catch (e) {
      toast.error("Save failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const pushNotification = async () => {
    if (!content?.notification_title?.trim()) {
      toast.error("Notification title is required");
      return;
    }
    await save({
      notification_title: content.notification_title,
      notification_content: content.notification_content || "",
      notification_active: true,
    });
    toast.success("Notification pushed — all devices will see it on next load");
  };

  const clearNotification = async () => save({ notification_active: false });

  const bumpDisclaimer = async () => {
    const nextV = Number(content?.disclaimer_version || 0) + 1;
    await save({
      disclaimer_active: true,
      disclaimer_version: nextV,
      disclaimer_text: content?.disclaimer_text,
    });
    toast.success(`All devices will re-prompt the disclaimer (v${nextV})`);
  };

  const uploadWallpaper = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append("name", file.name.replace(/\.[^.]+$/, ""));
    form.append("file", file);
    try {
      await axios.post(`${API}/admin/wallpaper`, form, {
        headers: { ...authHeader, "Content-Type": "multipart/form-data" },
      });
      const { data } = await axios.get(`${API}/wallpapers`);
      setWallpapers(data.items || []);
      toast.success("Wallpaper uploaded — all devices will see it");
    } catch (err) {
      toast.error("Upload failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const deleteWallpaper = async (id) => {
    try {
      await axios.delete(`${API}/admin/wallpaper/${id}`, { headers: authHeader });
      setWallpapers((ws) => ws.filter((w) => w.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  if (!content) {
    return <div className="text-zinc-400 p-6">Loading...</div>;
  }

  return (
    <div data-testid="admin-content-tab" className="space-y-8">
      {/* Push notification */}
      <section className="rounded-xl border border-white/10 bg-black/30 p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-cyan-400">Push notification (popup on every device)</h3>
          <span className="text-xs text-zinc-400">
            v{content.notification_version || 0} ·{" "}
            <span className={content.notification_active ? "text-green-400" : "text-zinc-500"}>
              {content.notification_active ? "ACTIVE" : "off"}
            </span>
          </span>
        </div>
        <input
          type="text"
          data-testid="notif-title"
          value={content.notification_title || ""}
          onChange={(e) => patch("notification_title", e.target.value)}
          placeholder="Notification title"
          className="w-full px-3 py-2 bg-black/50 border border-white/20 rounded text-white"
        />
        <textarea
          data-testid="notif-content"
          value={content.notification_content || ""}
          onChange={(e) => patch("notification_content", e.target.value)}
          placeholder="Body (HTML allowed)"
          rows={4}
          className="w-full px-3 py-2 bg-black/50 border border-white/20 rounded text-white font-mono text-sm"
        />
        <div className="flex gap-2">
          <button
            data-testid="notif-push-btn"
            onClick={pushNotification}
            disabled={saving}
            className="px-4 py-2 bg-cyan-500 text-black rounded font-medium hover:bg-cyan-400 disabled:opacity-50"
          >
            Push to all devices
          </button>
          <button
            data-testid="notif-clear-btn"
            onClick={clearNotification}
            disabled={saving || !content.notification_active}
            className="px-4 py-2 bg-white/10 text-white rounded hover:bg-white/20 disabled:opacity-50"
          >
            Clear active
          </button>
        </div>
      </section>

      {/* Editable text fields */}
      <section className="rounded-xl border border-white/10 bg-black/30 p-5 space-y-4">
        <h3 className="text-lg font-medium text-cyan-400">App content (shown in Settings + bootstrap)</h3>
        {FIELDS.map(([k, label]) => (
          <div key={k}>
            <label className="text-zinc-400 text-sm">{label}</label>
            <textarea
              data-testid={`content-${k}`}
              value={content[k] || ""}
              onChange={(e) => patch(k, e.target.value)}
              rows={3}
              className="w-full mt-1 px-3 py-2 bg-black/50 border border-white/20 rounded text-white font-mono text-xs"
            />
          </div>
        ))}
        <div className="flex items-center gap-4">
          <label className="text-zinc-400 text-sm">Trial days:</label>
          <input
            type="number"
            min={1}
            max={365}
            value={content.trial_days || 7}
            onChange={(e) => patch("trial_days", e.target.value)}
            className="w-24 px-3 py-2 bg-black/50 border border-white/20 rounded text-white"
            data-testid="content-trial-days"
          />
          <button
            data-testid="disclaimer-bump-btn"
            onClick={bumpDisclaimer}
            disabled={saving}
            className="px-3 py-2 bg-white/10 text-white rounded text-sm hover:bg-white/20 disabled:opacity-50"
            title={`Current disclaimer version: v${content.disclaimer_version || 1}`}
          >
            Re-prompt disclaimer (v{content.disclaimer_version || 1})
          </button>
          <button
            data-testid="content-save-btn"
            onClick={() => save()}
            disabled={saving}
            className="ml-auto px-4 py-2 bg-cyan-500 text-black rounded font-medium hover:bg-cyan-400 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save all fields"}
          </button>
        </div>
      </section>

      {/* Server-pushed wallpapers */}
      <section className="rounded-xl border border-white/10 bg-black/30 p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-cyan-400">Server wallpapers (pushed to every device)</h3>
          <label className="inline-flex items-center gap-2 px-3 py-2 border border-cyan-400/40 text-cyan-300 rounded-lg cursor-pointer hover:bg-cyan-400/10 text-sm">
            {uploading ? "Uploading…" : "+ Upload"}
            <input type="file" accept="image/*" className="hidden" onChange={uploadWallpaper} data-testid="admin-wp-upload" />
          </label>
        </div>
        {wallpapers.length === 0 ? (
          <div className="text-zinc-500 text-sm">No server wallpapers yet — uploaded images here show up in every user's theme picker.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {wallpapers.map((w) => (
              <div key={w.id} className="relative aspect-video rounded-lg overflow-hidden border border-white/10 group">
                <img src={`${BACKEND_URL}${w.url}`} alt={w.name} className="w-full h-full object-cover" loading="lazy" />
                <span className="absolute bottom-1 left-1 right-8 text-[10px] bg-black/60 text-white rounded px-1 py-0.5 truncate">
                  {w.name}
                </span>
                <button
                  data-testid={`admin-wp-delete-${w.id}`}
                  onClick={() => deleteWallpaper(w.id)}
                  className="absolute top-1 right-1 w-6 h-6 grid place-items-center bg-red-600/80 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Delete"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Watch stats */}
      <section className="rounded-xl border border-white/10 bg-black/30 p-5 space-y-4">
        <h3 className="text-lg font-medium text-cyan-400">Watch telemetry (last 7 days)</h3>
        {stats ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <Stat label="Total plays"     value={stats.total_plays} />
              <Stat label="Active devices"  value={stats.active_devices} />
              <Stat label="Categories"      value={stats.by_category.length} />
              <Stat label="Window"          value={`${stats.days} days`} />
            </div>
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <div>
                <div className="text-xs text-zinc-400 mb-2">Top channels</div>
                <div className="space-y-1">
                  {stats.top_channels.slice(0, 10).map((t, i) => (
                    <div key={i} className="flex justify-between text-sm border-b border-white/5 py-1">
                      <span className="truncate pr-2">{t.channel_name}</span>
                      <span className="text-zinc-400 whitespace-nowrap">
                        {t.plays} × · {Math.round((t.seconds || 0) / 60)} min
                      </span>
                    </div>
                  ))}
                  {stats.top_channels.length === 0 && (
                    <div className="text-zinc-500 text-sm">No plays yet.</div>
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs text-zinc-400 mb-2">By category</div>
                <div className="space-y-1">
                  {stats.by_category.map((c, i) => (
                    <div key={i} className="flex justify-between text-sm border-b border-white/5 py-1">
                      <span>{c.category}</span>
                      <span className="text-zinc-400">{c.plays}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="text-zinc-500 text-sm">Loading stats…</div>
        )}

        <div className="mt-4">
          <div className="text-xs text-zinc-400 mb-2">Recent plays ({logs.total} total)</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-zinc-500">
                <tr><th className="text-left py-1">Time</th><th className="text-left py-1">Device</th><th className="text-left py-1">Channel</th><th className="text-left py-1">Cat</th><th className="text-right py-1">Duration</th></tr>
              </thead>
              <tbody>
                {logs.items.map((l) => (
                  <tr key={l.id} className="border-t border-white/5">
                    <td className="py-1 text-zinc-400">{new Date(l.created_at).toLocaleString()}</td>
                    <td className="py-1 font-mono">{l.device_key}</td>
                    <td className="py-1 truncate max-w-[200px]">{l.channel_name}</td>
                    <td className="py-1">{l.category}</td>
                    <td className="py-1 text-right text-zinc-400">{l.duration_seconds}s</td>
                  </tr>
                ))}
                {logs.items.length === 0 && (
                  <tr><td colSpan={5} className="py-4 text-center text-zinc-500">No watch events yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg border border-white/10 bg-black/30 p-3">
      <div className="text-xs text-zinc-500">{label}</div>
      <div className="text-xl font-semibold text-white mt-1">{value}</div>
    </div>
  );
}
